/**
 * Turns a stored `VideoEffectPreferences` into everything the compositor needs
 * — a loaded segmenter and a decoded background — or into a runtime status that
 * explains, in one sentence a user can act on, why the effect is not running.
 *
 * NOTHING here throws. A background effect is a nicety; failing to load one may
 * never cost anyone their camera.
 */
import type { VideoEffectPreferences, VideoEffectRuntime } from '../settings/preferences';
import { isBuiltinBackground, renderBuiltinBackground } from './backgroundCatalog';
import { findCustomBackground, isCustomBackground } from './backgroundStore';
import type { ResolvedEffect } from './effectPipeline';
import { loadPersonSegmenter, segmentationBlock, type PersonSegmenter } from './segmenter';

const BLUR_PX: Readonly<Record<'light' | 'strong', number>> = { light: 9, strong: 18 };
const BACKGROUND_WIDTH = 1280;
const BACKGROUND_HEIGHT = 720;

const REASONS = {
  unsupported: 'Background effects need a newer browser or a more powerful device.',
  'save-data': 'Background effects stay off while your browser is in data-saver mode.',
  'load-failed': 'Background effects could not be loaded. Your camera is unchanged.',
  'missing-image': 'That background image is no longer saved on this device.',
} as const;

export interface ResolvedVideoEffect {
  readonly effect: ResolvedEffect;
  readonly segmenter: PersonSegmenter;
}

export type EffectResolution =
  | { readonly ok: true; readonly value: ResolvedVideoEffect }
  | { readonly ok: false; readonly runtime: VideoEffectRuntime };

const failure = (
  kind: VideoEffectPreferences['kind'],
  reason: string,
): EffectResolution => ({
  ok: false,
  runtime: { kind, active: false, degraded: true, reason, fps: 0 },
});

async function decodeImage(dataUrl: string): Promise<HTMLImageElement | null> {
  const image = new Image();
  image.src = dataUrl;
  try {
    await image.decode();
    return image;
  } catch {
    return null;
  }
}

async function resolveBackground(id: string | null): Promise<CanvasImageSource | null> {
  if (id === null) return null;
  if (isCustomBackground(id)) {
    const stored = findCustomBackground(id);
    return stored ? decodeImage(stored.dataUrl) : null;
  }
  if (isBuiltinBackground(id)) {
    return renderBuiltinBackground(id, BACKGROUND_WIDTH, BACKGROUND_HEIGHT);
  }
  return null;
}

export async function resolveVideoEffect(
  preferences: VideoEffectPreferences,
): Promise<EffectResolution> {
  const blocked = segmentationBlock();
  if (blocked !== null) return failure(preferences.kind, REASONS[blocked]);

  const segmenter = await loadPersonSegmenter();
  if (!segmenter) return failure(preferences.kind, REASONS['load-failed']);

  if (preferences.kind === 'image') {
    const background = await resolveBackground(preferences.backgroundId);
    if (!background) return failure('image', REASONS['missing-image']);
    return { ok: true, value: { effect: { kind: 'image', blurPx: 0, background }, segmenter } };
  }

  return {
    ok: true,
    value: {
      effect: { kind: 'blur', blurPx: BLUR_PX[preferences.blurStrength], background: null },
      segmenter,
    },
  };
}
